import request from "../../../util/SuperagentUtils";
import type { Response } from "superagent";
import type { Account } from '../../../types/Account';
import { hasAdminAccess } from "../../../util/AccountUtils";
import { securSpacePlatforms } from "../../../components/constants/securspace-constants";

const LOCAL_TESTING_MODE = false; // Set to true to enable local testing mode

const FILE_NAMES = {
  STANDARD: "locations.json",
  ADMIN: "all_locations.json",
} as const;

const CLOUDFRONT_CDN_URL = "https://static.secur.space/data/";

// =============================================================================
// Environment Configuration
// =============================================================================

/**
 * Build the full URL for a given environment
 */
const buildUrl = (baseUrl: string, environment?: string): string => {
  return environment ? `${baseUrl}${environment}/` : baseUrl;
};

/**
 * Configuration for each environment
 */
const ENVIRONMENT_CONFIG = {
  LOCAL: {
    test: securSpacePlatforms.LOCAL.hostnameTest,
    useCache: LOCAL_TESTING_MODE,
    url: buildUrl(CLOUDFRONT_CDN_URL, securSpacePlatforms.TEST.name),
  },
  TEST: {
    test: securSpacePlatforms.TEST.hostnameTest,
    useCache: true,
    url: buildUrl(CLOUDFRONT_CDN_URL, securSpacePlatforms.TEST.name),
  },
  SANDBOX: {
    test: securSpacePlatforms.SANDBOX.hostnameTest,
    useCache: true,
    url: buildUrl(CLOUDFRONT_CDN_URL, securSpacePlatforms.SANDBOX.name),
  },
  PRODUCTION: {
    test: securSpacePlatforms.PRODUCTION.hostnameTest,
    useCache: true,
    url: CLOUDFRONT_CDN_URL,
  },
} as const;

// =============================================================================
// TypeScript Interfaces
// =============================================================================

/**
 * Structure of cached location data from CDN matching the locations.json format
 */
interface CachedLocationData {
  id: string;
  lat: number;
  lon: number;
  locationName: string;
  city: string;
  state: string;
  active: boolean;
  lastUpdated: string;
  pricePerDay?: number;
  pricePerMonth?: number;
  managedSpaces: number;
  totalSpacesBooked: number;
  hasInstantApproval: boolean;
  listingImageFileName: string;
  gallery: string[];
  features: string[];
  equipmentTypes: string[];
}

/**
 * Response structure from the cached locations endpoint
 */
interface CachedLocationsResponse {
  locations: CachedLocationData[];
}

/**
 * Superagent response type for cached locations
 */
type CachedLocationsSuperagentResponse = Response & {
  body: CachedLocationsResponse;
};

// =============================================================================
// In-Memory Cache
// =============================================================================

let cachedLocationsUrl: string | null = null;
let cachedLocationsData: CachedLocationsSuperagentResponse | null = null;
let cachedLocationsPromise: Promise<CachedLocationsSuperagentResponse> | null = null;

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get the environment configuration for the current hostname
 * @param hostname - The current hostname
 * @returns Environment configuration (defaults to PRODUCTION if no match)
 */
const getEnvironmentConfig = (hostname: string) => {
  return Object.values(ENVIRONMENT_CONFIG).find(config => config.test.test(hostname))
    || ENVIRONMENT_CONFIG.PRODUCTION;
};

/**
 * Determine if cached supplier locations should be used based on the hostname
 * @param hostname - The current hostname
 * @returns True if cache should be used, false otherwise
 */
const shouldUseCache = (hostname: string): boolean => {
  return getEnvironmentConfig(hostname).useCache;
};

/**
 * Get the appropriate filename based on user permissions
 * @param user - The current user account
 * @returns Either 'locations.json' or 'all_locations.json' based on admin access
 */
const getFileName = (user: Account | null | undefined): string => {
  return hasAdminAccess(user) ? FILE_NAMES.ADMIN : FILE_NAMES.STANDARD;
};

/**
 * Build the complete URL for fetching cached locations
 * @param user - The current user account
 * @param hostname - The current hostname
 * @returns The full URL to fetch cached locations from
 */
const getLocationsUrl = (user: Account | null | undefined, hostname: string): string => {
  const config = getEnvironmentConfig(hostname);
  return `${config.url}${getFileName(user)}`;
};

/**
 * Fetch cached supplier locations from the appropriate s3 URL.
 * Uses in-memory caching to ensure the file is only fetched once per session.
 * @returns {Promise<CachedLocationsSuperagentResponse>} - A promise that resolves to the fetched locations data.
 */
const getCachedLocationSuppliers = (user: Account | null | undefined, hostname: string): Promise<CachedLocationsSuperagentResponse> => {
  const url = getLocationsUrl(user, hostname);
  if (cachedLocationsUrl !== url) {
    // desired cache filepath has changed, clear existing cache and fetch new data
    clearCachedLocations();
  } else if (cachedLocationsData) {
    // Return cached data if already loaded
    return Promise.resolve(cachedLocationsData);
  } else if (cachedLocationsPromise) {
    // If a fetch is already in progress, return that promise to avoid duplicate requests
    return cachedLocationsPromise;
  }

  // Fetch the data and cache both the promise and the result
  cachedLocationsUrl = url;
  cachedLocationsPromise = request.get(cachedLocationsUrl)
    .then((response: CachedLocationsSuperagentResponse) => {
      cachedLocationsData = response;
      return response;
    })
    .finally(() => {
      // Clear the promise after completion (success or error) to allow retry
      cachedLocationsPromise = null;
    });

  return cachedLocationsPromise;
}

/**
 * Clear the in-memory cache. Useful for testing or forcing a refresh.
 */
const clearCachedLocations = (): void => {
  cachedLocationsUrl = null;
  cachedLocationsData = null;
  cachedLocationsPromise = null;
}

export {
  shouldUseCache,
  getCachedLocationSuppliers,
  clearCachedLocations
}
